import { useState, useEffect, useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, Kanban as KanbanIcon, ChartBar } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Epic, EpicStatus, EpicProgress } from 'shared/types/epic';
import { getAllEpics, getEpicRoadmap } from 'shared/services/epicService';
import { EpicKanbanView } from './components/EpicKanbanView';
import { EpicGanttView } from './components/EpicGanttView';
import { CreateEpicModal } from './components/CreateEpicModal';
import * as Sentry from '@sentry/react';

type ViewMode = 'kanban' | 'gantt';

export const EpicsOverviewPage = () => {
  const { session } = useSession();
  const navigate = useNavigate();

  const [epics, setEpics] = useState<Epic[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [progressData, setProgressData] = useState<Map<number, EpicProgress>>(
    new Map()
  );

  // Fetch epics and their progress data
  const fetchEpics = async () => {
    setLoading(true);
    try {
      const data = await getAllEpics(session);
      setEpics(data);

      // Fetch progress for each epic
      const progressMap = new Map<number, EpicProgress>();
      await Promise.all(
        data.map(async (epic) => {
          try {
            const roadmapData = await getEpicRoadmap(epic.id, session);
            progressMap.set(epic.id, roadmapData.progress);
          } catch (error) {
            console.error(`Failed to fetch progress for epic ${epic.id}:`, error);
            Sentry.captureException(error, {
              tags: { screen: 'EpicsOverview' },
              extra: { epicId: epic.id }
            });
          }
        })
      );

      setProgressData(progressMap);
    } catch (error) {
      console.error('Error fetching epics:', error);
      Sentry.captureException(error, {
        tags: { screen: 'EpicsOverview' }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEpics();
  }, [session]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = epics.length;
    const byStatus = {
      planning: epics.filter((e) => e.status === EpicStatus.PLANNING).length,
      inProgress: epics.filter((e) => e.status === EpicStatus.IN_PROGRESS)
        .length,
      completed: epics.filter((e) => e.status === EpicStatus.COMPLETED).length,
      onHold: epics.filter((e) => e.status === EpicStatus.ON_HOLD).length
    };

    return { total, byStatus };
  }, [epics]);

  const handleEpicClick = (epic: Epic) => {
    navigate(`/bug-reports/epics/${epic.id}`);
  };

  const handleCreateSuccess = () => {
    fetchEpics();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Laden...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Epics Overzicht</h1>
          <p className="text-neutral-600 text-sm mt-1">
            Beheer en volg grote projecten en initiatieven
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon size={20} className="mr-2" />
          Nieuwe Epic
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-sm text-neutral-600">Totaal</div>
          <div className="text-2xl font-bold mt-1">{stats.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-blue-700">Planning</div>
          <div className="text-2xl font-bold mt-1 text-blue-700">
            {stats.byStatus.planning}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-moss-primary">In Behandeling</div>
          <div className="text-2xl font-bold mt-1 text-moss-primary">
            {stats.byStatus.inProgress}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-status-good">Afgerond</div>
          <div className="text-2xl font-bold mt-1 text-status-good">
            {stats.byStatus.completed}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-orange-700">On Hold</div>
          <div className="text-2xl font-bold mt-1 text-orange-700">
            {stats.byStatus.onHold}
          </div>
        </Card>
      </div>

      {/* View Mode Selector */}
      <div className="flex items-center gap-2">
        <Button
          variant={viewMode === 'kanban' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('kanban')}
        >
          <KanbanIcon size={16} className="mr-2" />
          Kanban
        </Button>
        <Button
          variant={viewMode === 'gantt' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('gantt')}
        >
          <ChartBar size={16} className="mr-2" />
          Gantt
        </Button>
      </div>

      {/* Content */}
      {viewMode === 'kanban' ? (
        <EpicKanbanView
          epics={epics}
          onUpdate={fetchEpics}
          onEpicClick={handleEpicClick}
          progressData={progressData}
        />
      ) : (
        <EpicGanttView epics={epics} onEpicClick={handleEpicClick} />
      )}

      {/* Create Modal */}
      <CreateEpicModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
